import { ChildModel, ModelBase } from 'store/model-base';

import { PaymentState, TransactionType } from 'my-account/constants';
import { PaymentInfo } from 'store/credit-card/models';
import { Transaction } from 'store/transaction/models';
import { CmsBillingCredit } from 'store/cms/models';

export class PaymentSummary extends ModelBase {
    public static storeName: string = 'paymentSummary';
    public static idAttribute: string = 'id.key';

    public id: Id;
    public type: TransactionType;
    public state: PaymentState;
    public amount: number;
    public transactionDate: string;
    public title: string;
    public subtitle: string;
    public transactions: Transaction[];  // will be sent only for type = BALANCE_PAYMENT, otherwise could be null
    public paymentInfo: PaymentInfo;

    public isPaid: boolean;
    public isBalancePayment: boolean;
    public isOneTimePayment: boolean;
    public isQuickPayment: boolean;
    public isRefund: boolean;
    public isCredit: boolean;
    public isReversalCredit: boolean;
    public isNextPayment: boolean;

    public hasMultipleTransactions: boolean;
    public displayTotal: number;

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'paymentInfo',
            model: PaymentInfo
        }];
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'transactions',
            model: Transaction
        }];
    }

    public static create<T extends ModelBase>(initData: object): T {
        const payment: PaymentSummary = super.create<PaymentSummary>(initData);

        payment.isPaid = payment.state === PaymentState.PAID;
        payment.isNextPayment = payment.state === PaymentState.PENDING;
        payment.isBalancePayment = payment.type === TransactionType.BALANCE_PAYMENT;
        payment.isOneTimePayment = payment.type === TransactionType.ONETIME_PAYMENT;
        payment.isQuickPayment = payment.type === TransactionType.QUICK_PAYMENT;
        payment.isRefund = payment.type === TransactionType.REFUND;
        payment.isCredit = payment.type === TransactionType.CREDIT;
        payment.isReversalCredit = payment.type === TransactionType.CREDIT_REVERSAL;

        payment.displayTotal = payment.isPaid ? payment.paymentInfo.amount : payment.amount;

        payment.hasMultipleTransactions = payment.transactions && payment.transactions.length > 1;

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> payment;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    public get paymentCycle(): string {
        const transactionList: Transaction[] = this.transactions
            .filter((transaction: Transaction) => transaction.type === TransactionType.STATEMENT)
            .sort((a: Transaction, b: Transaction) => a.transactionDate < b.transactionDate ? -1 : 1);

        const first: string = transactionList && transactionList[0].title.match(/\w{3}\s\d{1,2}/g)[0];
        const last: string = transactionList && transactionList.slice(-1)[0].title.match(/\w{3}\s\d{1,2}/g)[1];

        return `${first} - ${last}`;
    }

    public getCreditPageHeading(cmsData: CmsBillingCredit): string {
        switch (true) {
            case this.isRefund:
                return cmsData.refund.title;
            case this.isCredit:
                return cmsData.credit.title;
            case this.isReversalCredit:
                return cmsData.reversalCredit.title;
        }
    }

    public getPaymentHeading(cmsData: CmsBillingPaymentState): string {
        return this.isNextPayment ? cmsData.header : cmsData.header.replace('[CYCLE]', this.paymentCycle);
    }
}
